import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Search, X } from "lucide-react";
import ProductActions from "../components/common/ProductActions";
import { catalogService, analyticsService, resolveImageUrl } from "../services/catalogService";
import { useLanguage } from "../context/LanguageContext";

// Task 03 (C03) — catalog search with multi-select filters.
// Logic: OR within each filter group, AND across groups.
// Task 05 (C05) — filters can be pre-selected via URL, e.g. /catalog?categories=roses
function toggle(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function splitParam(value) {
  return value ? value.split(",").map((v) => v.trim()).filter(Boolean) : [];
}

export default function CatalogPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(() => searchParams.get("q") || "");
  const [colors, setColors] = useState(() => splitParam(searchParams.get("colors")));
  const [categories, setCategories] = useState(() => splitParam(searchParams.get("categories")));
  const [varieties, setVarieties] = useState(() => splitParam(searchParams.get("varieties")));
  const debounceRef = useRef(null);

  useEffect(() => {
    catalogService.getFacets().then(setFacets).catch(() => setFacets({ colors: [], categories: [], varieties: [] }));
  }, []);

  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => window.clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setItems(null);
    catalogService
      .listVarieties({ q: debouncedQ, colors, categories, varieties })
      .then((data) => {
        if (!alive) return;
        setItems(data);
        // Task 22 — record catalog searches (anonymous, best-effort)
        if (debouncedQ && debouncedQ.length >= 2) {
          analyticsService.trackSearch(debouncedQ, data.length);
        }
      })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [debouncedQ, colors, categories, varieties]);

  const hasFilters = Boolean(debouncedQ) || colors.length > 0 || categories.length > 0 || varieties.length > 0;
  const clearAll = useCallback(() => {
    setQ("");
    setColors([]);
    setCategories([]);
    setVarieties([]);
  }, []);

  return (
    <main className="min-h-screen bg-[#F6F2EC] text-[#252525] pt-28 pb-24" data-testid="catalog-page">
      <div className="container-tessa">
        <Link to="/" className="flex w-fit items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#817B74] hover:text-[#D92A47] transition-colors mb-10">
          <ArrowLeft size={14} /> {t("pages.backHome")}
        </Link>

        <div className="max-w-3xl">
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">{t("pages.catalog.eyebrow")}</span>
          <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] mt-3">{t("pages.catalog.title")}</h1>
          <p className="text-[#817B74] mt-4">{t("pages.catalog.subtitle")}</p>
        </div>

        <div className="relative max-w-xl mt-10">
          <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#817B74]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("pages.catalog.searchPlaceholder")}
            data-testid="catalog-search"
            className="w-full bg-transparent border-b border-black/15 pl-7 py-3 focus:outline-none focus:border-[#D92A47] placeholder:text-[#817B74]/70"
          />
        </div>

        <div className="mt-10 space-y-8" data-testid="catalog-filters">
          {facets?.colors?.length > 0 && (
            <div>
              <span className="uppercase tracking-[0.14em] text-[11px] text-[#817B74]">{t("pages.catalog.color")}</span>
              <div className="flex flex-wrap gap-3 mt-3">
                {facets.colors.map((c) => {
                  const on = colors.includes(c.family);
                  return (
                    <button
                      key={c.family}
                      type="button"
                      onClick={() => setColors((prev) => toggle(prev, c.family))}
                      aria-pressed={on}
                      data-testid={`filter-color-${c.family}`}
                      className={`flex items-center gap-2 h-10 px-3 border text-[12px] transition-colors ${
                        on ? "border-[#D92A47] bg-[#D92A47]/5 text-[#252525]" : "border-black/15 text-[#817B74] hover:border-[#D92A47]/60"
                      }`}
                    >
                      <span className={`h-5 w-5 rounded-full ring-1 ring-black/10 ${on ? "ring-2 ring-[#D92A47]" : ""}`} style={{ backgroundColor: c.hex }} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {facets?.categories?.length > 0 && (
            <div>
              <span className="uppercase tracking-[0.14em] text-[11px] text-[#817B74]">{t("pages.catalog.subcategory")}</span>
              <div className="flex flex-wrap gap-2 mt-3">
                {facets.categories.map((c) => {
                  const on = categories.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategories((prev) => toggle(prev, c.id))}
                      aria-pressed={on}
                      data-testid={`filter-category-${c.id}`}
                      className={`h-10 px-4 border text-[12px] uppercase tracking-[0.1em] transition-colors ${
                        on ? "border-[#D92A47] bg-[#D92A47] text-[#F6F2EC]" : "border-black/15 text-[#817B74] hover:border-[#D92A47]/60"
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {facets?.varieties?.length > 0 && (
            <div>
              <span className="uppercase tracking-[0.14em] text-[11px] text-[#817B74]">{t("pages.catalog.variety")}</span>
              <div className="flex flex-wrap gap-2 mt-3">
                {facets.varieties.map((v) => {
                  const on = varieties.includes(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setVarieties((prev) => toggle(prev, v.id))}
                      aria-pressed={on}
                      data-testid={`filter-variety-${v.id}`}
                      className={`h-10 px-4 border font-serif italic text-[15px] transition-colors ${
                        on ? "border-[#D92A47] bg-[#D92A47] text-[#F6F2EC]" : "border-black/15 text-[#817B74] hover:border-[#D92A47]/60"
                      }`}
                    >
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 mt-12 mb-6">
          <span className="text-[12px] uppercase tracking-[0.14em] text-[#817B74]" data-testid="catalog-results-count">
            {items === null ? t("pages.loading") : `${items.length} ${items.length === 1 ? t("pages.catalog.resultOne") : t("pages.catalog.resultMany")}`}
          </span>
          {hasFilters && (
            <button
              onClick={clearAll}
              data-testid="catalog-clear-filters"
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.14em] text-[#D92A47] hover:text-[#b71e3a]"
            >
              <X size={13} /> {t("pages.catalog.clearFilters")}
            </button>
          )}
        </div>

        {items === null ? (
          <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />{t("pages.loading")}</div>
        ) : items.length === 0 ? (
          <div className="border border-black/10 bg-white p-12 text-center" data-testid="catalog-empty">
            <p className="font-serif text-2xl">{t("pages.catalog.emptyTitle")}</p>
            <p className="text-[#817B74] mt-2">{t("pages.catalog.emptyText")}</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-results">
            {items.map((v) => (
              <li key={v.id} className="bg-white border border-black/10 flex flex-col" data-testid={`catalog-card-${v.id}`}>
                <Link to={`/variety/${v.id}`} className="block aspect-[4/3] overflow-hidden bg-[#F6F2EC]">
                  {v.image && <img src={resolveImageUrl(v.image)} alt={v.name} className="h-full w-full object-cover hover:scale-[1.03] transition-transform duration-500" />}
                </Link>
                <div className="p-5 flex-1 flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">{v.category_label}</span>
                  <h3 className="font-serif text-2xl text-[#252525] mt-1">
                    <Link to={`/variety/${v.id}`} className="hover:text-[#D92A47] transition-colors">{v.name}</Link>
                  </h3>
                  <div className="flex items-center gap-2 mt-auto pt-6">
                    <ProductActions
                      dense
                      variety={{ id: v.id, name: v.name, image: v.image, category_label: v.category_label }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
